import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Select, Button, Alert, message } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSync, faTrash } from "@fortawesome/free-solid-svg-icons";
import postApi from "../../../../api/postApi";
import VideoProductPreview from "./video-product-preview";
import VideoStatusTag from "./video-status-tag";

const MAX_PRODUCTS = 5;

// Form thêm/sửa video: link video + tên + danh mục + 1-5 link sản phẩm Shopee.
// "Lấy thông tin video" -> resolve metadata (thumbnail/caption/author + còn sống/đã gỡ).
// Mỗi link SP có nút "Lấy thông tin" -> preview + cảnh báo trùng. Preview key theo URL (ổn định khi thêm/xoá dòng).
function VideoProductFormModal({ open, onClose, onSaved, categories, editingVideo }) {
    const [form] = Form.useForm();
    const [productPreviews, setProductPreviews] = useState({}); // url -> { product, duplicate }
    const [videoPreview, setVideoPreview] = useState(null); // { status, thumbnail, caption, author }
    const [videoDuplicate, setVideoDuplicate] = useState(null); // { videoId, videoName } nếu trùng theo video_id
    const [resolving, setResolving] = useState({}); // url -> bool
    const [videoResolving, setVideoResolving] = useState(false);
    const [saving, setSaving] = useState(false);
    const products = Form.useWatch("products", form) || [];
    const isEdit = !!editingVideo;

    useEffect(() => {
        if (!open) return;
        setProductPreviews({});
        setVideoPreview(null);
        setVideoDuplicate(null);
        if (editingVideo) {
            form.setFieldsValue({
                url: editingVideo.url,
                productName: editingVideo.productName,
                categoryId: editingVideo.categoryId || "all",
                products: (editingVideo.products || []).map((p) => ({
                    url: p.originalUrl || p.productLink || "",
                })),
            });
            // Seed preview theo URL
            const seeded = {};
            (editingVideo.products || []).forEach((p) => {
                const k = p.originalUrl || p.productLink;
                if (k) seeded[k] = { product: p, duplicate: null };
            });
            setProductPreviews(seeded);
            if (editingVideo.thumbnail || editingVideo.videoStatus) {
                setVideoPreview({
                    status: editingVideo.videoStatus,
                    thumbnail: editingVideo.thumbnail,
                    caption: editingVideo.caption,
                    author: editingVideo.author,
                });
            }
        } else {
            form.resetFields();
            form.setFieldsValue({ categoryId: "all", products: [{ url: "" }] });
        }
    }, [open, editingVideo, form]);

    const resolveVideoMeta = async () => {
        const url = form.getFieldValue("url");
        if (!url) {
            message.warning("Nhập link video trước");
            return;
        }
        setVideoResolving(true);
        try {
            const { data } = await postApi.resolveVideo(url);
            setVideoPreview(data.video);
            // Lấy được info → đổi ô link sang link share-video ổn định.
            if (data.video?.canonicalUrl) {
                form.setFieldValue("url", data.video.canonicalUrl);
            }
            // Bỏ qua cảnh báo trùng nếu chính là video đang sửa.
            const dup = data.duplicate;
            setVideoDuplicate(dup && (!isEdit || dup.videoId !== editingVideo?.id) ? dup : null);
        } catch (e) {
            message.error("Không lấy được thông tin video");
        } finally {
            setVideoResolving(false);
        }
    };

    const resolveRow = async (name, url) => {
        if (!url) {
            message.warning("Nhập link sản phẩm trước");
            return;
        }
        setResolving((p) => ({ ...p, [url]: true }));
        try {
            const { data } = await postApi.resolveProduct(url);
            // Lấy được info → đổi ô link sang link sản phẩm đầy đủ (shopee.vn/product/...).
            const fullUrl = data.product?.productLink || url;
            setProductPreviews((p) => ({
                ...p,
                [fullUrl]: { product: data.product, duplicate: data.duplicate },
            }));
            if (data.product?.productLink && fullUrl !== url) {
                form.setFieldValue(["products", name, "url"], fullUrl);
            }
        } catch (e) {
            message.error("Không lấy được thông tin sản phẩm");
        } finally {
            setResolving((p) => ({ ...p, [url]: false }));
        }
    };

    const handleSubmit = async (values) => {
        const list = (values.products || [])
            .filter((p) => p && p.url && p.url.trim())
            .slice(0, MAX_PRODUCTS)
            .map((p) => ({ url: p.url.trim() }));

        setSaving(true);
        try {
            if (isEdit) {
                await postApi.updateVideoList(editingVideo.id, {
                    url: values.url,
                    productName: values.productName,
                    categoryId: values.categoryId || "all",
                });
                await postApi.updateVideoProducts(editingVideo.id, list);
                message.success("Đã cập nhật video");
            } else {
                await postApi.createVideoWithProducts({
                    url: values.url,
                    productName: values.productName,
                    categoryId: values.categoryId || "all",
                    products: list,
                });
                message.success("Đã thêm video");
            }
            onSaved();
            onClose();
        } catch (e) {
            message.error("Không thể lưu video");
        } finally {
            setSaving(false);
        }
    };

    const categoryOptions = categories.map((c) => ({
        label: c.categoryName || c.categoryId,
        value: c.categoryId,
    }));

    return (
        <Modal
            title={isEdit ? "Sửa Video" : "Thêm Video"}
            open={open}
            onCancel={onClose}
            footer={null}
            width={760}
            style={{ top: 16, maxWidth: "calc(100vw - 16px)" }}
            destroyOnClose
        >
            <Form form={form} layout="vertical" onFinish={handleSubmit}>
                {/* Mobile: nút xếp xuống dưới input; desktop: nằm cạnh */}
                <div className="flex flex-col sm:flex-row sm:gap-2 sm:items-end">
                    <Form.Item
                        name="url"
                        label="Link video (Shopee Video)"
                        className="flex-1 mb-2 sm:mb-6"
                        rules={[{ required: true, message: "Nhập link video" }]}
                    >
                        <Input placeholder="https://vn.shp.ee/..." />
                    </Form.Item>
                    <Form.Item className="mb-4 sm:mb-6">
                        <Button block icon={<FontAwesomeIcon icon={faSync} />} loading={videoResolving} onClick={resolveVideoMeta}>
                            Lấy thông tin video
                        </Button>
                    </Form.Item>
                </div>

                {videoDuplicate && (
                    <Alert
                        type="warning"
                        showIcon
                        className="mb-3"
                        message={`Video này đã có trong danh sách: #${videoDuplicate.videoId} (${videoDuplicate.videoName || ""}) — trùng theo video_id`}
                    />
                )}

                {videoPreview && (
                    <div className="flex gap-3 items-center border rounded p-2 mb-3 bg-surface-sunken">
                        <img
                            src={videoPreview.thumbnail || ""}
                            alt=""
                            className="w-14 h-20 object-cover rounded bg-surface-raised"
                            onError={(e) => (e.currentTarget.style.visibility = "hidden")}
                        />
                        <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{videoPreview.caption || "(không có tiêu đề)"}</div>
                            {videoPreview.author && <div className="text-xs text-content-3">@{videoPreview.author}</div>}
                            <div className="mt-1"><VideoStatusTag status={videoPreview.status} /></div>
                        </div>
                    </div>
                )}

                <div className="flex flex-col sm:flex-row sm:gap-2">
                    <Form.Item name="productName" label="Tiêu đề hiển thị" className="flex-1 mb-3 sm:mb-6">
                        <Input placeholder="Tên hiển thị (mặc định lấy caption video)" />
                    </Form.Item>
                    <Form.Item name="categoryId" label="Danh mục" className="w-full sm:w-48">
                        <Select options={categoryOptions} />
                    </Form.Item>
                </div>

                <p className="font-semibold mb-2">Sản phẩm gắn kèm (tuỳ chọn, tối đa {MAX_PRODUCTS})</p>
                <Form.List name="products">
                    {(fields, { add, remove }) => (
                        <>
                            {fields.map((field) => {
                                const rowUrl = products[field.name]?.url;
                                const pv = rowUrl ? productPreviews[rowUrl] : null;
                                return (
                                    <div key={field.key} className="border rounded p-3 mb-3">
                                        {/* Mobile: input full dòng, nút xếp dưới; desktop: cùng 1 hàng */}
                                        <div className="flex flex-wrap gap-2 items-start">
                                            {/* Không bắt buộc — có thể tạo video trước, gắn sản phẩm sau. Dòng trống bị lọc khi lưu. */}
                                            <Form.Item name={[field.name, "url"]} className="w-full sm:w-auto sm:flex-1 mb-2">
                                                <Input placeholder="Link sản phẩm Shopee (vn.shp.ee/... hoặc link đầy đủ) — bỏ trống nếu thêm sau" />
                                            </Form.Item>
                                            <div className="flex gap-2 flex-1 sm:flex-none mb-2 sm:mb-0">
                                                <Button
                                                    className="flex-1 sm:flex-none"
                                                    icon={<FontAwesomeIcon icon={faSync} />}
                                                    loading={!!resolving[rowUrl]}
                                                    onClick={() => resolveRow(field.name, rowUrl)}
                                                >
                                                    Lấy thông tin
                                                </Button>
                                                <Button danger type="text" icon={<FontAwesomeIcon icon={faTrash} />} onClick={() => remove(field.name)} />
                                            </div>
                                        </div>
                                        {pv?.duplicate && (
                                            <Alert
                                                type="warning"
                                                showIcon
                                                className="mb-2"
                                                message={`Trùng: sản phẩm này đã gắn ở video #${pv.duplicate.videoId} (${pv.duplicate.videoName || ""})`}
                                            />
                                        )}
                                        {pv?.product && <VideoProductPreview product={pv.product} />}
                                    </div>
                                );
                            })}
                            {fields.length < MAX_PRODUCTS && (
                                <Button type="dashed" block className="mb-4" onClick={() => add({ url: "" })}>
                                    + Thêm sản phẩm
                                </Button>
                            )}
                        </>
                    )}
                </Form.List>

                <div className="flex justify-end gap-2">
                    <Button onClick={onClose}>Huỷ</Button>
                    <Button type="primary" htmlType="submit" loading={saving}>
                        {isEdit ? "Cập nhật" : "Thêm"}
                    </Button>
                </div>
            </Form>
        </Modal>
    );
}

export default VideoProductFormModal;
